/**
 * カテゴリカル変数のエンコーディングユーティリティ
 *
 * GLMなどの統計モデルでstring型のカテゴリカル変数を
 * ダミー変数（Treatment coding）に変換する
 */

import type { DataValue } from '../../types';
import logger from './logger';

/**
 * カテゴリカル変数のエンコーディング情報
 */
export interface CategoricalEncoding {
  /** 元の列ID */
  originalColumnId: string;
  /** 元の列名 */
  originalColumnName: string;
  /** すべてのカテゴリ（アルファベット順） */
  categories: string[];
  /** 参照カテゴリ（最初のカテゴリ） */
  referenceCategory: string;
  /** ダミー変数名の配列（参照カテゴリを除く） */
  dummyVariableNames: string[];
}

/**
 * カテゴリカル変数をダミー変数に変換した結果
 */
export interface EncodedCategoricalVariable {
  /** エンコーディング情報 */
  encoding: CategoricalEncoding;
  /** ダミー変数の列データ（各列は0/1の配列） */
  dummyColumns: number[][];
}

/**
 * カテゴリカル変数のダミー変数化を実行
 *
 * Treatment coding（k-1個のダミー変数）を使用。
 * アルファベット順で最初のカテゴリを参照カテゴリとする。
 *
 * @param columnData - カテゴリカル変数のデータ（string配列）
 * @param columnId - 列ID
 * @param columnName - 列名
 * @returns エンコード結果（nullが含まれる行はnullのまま）
 */
export function encodeCategoricalVariable(
  columnData: DataValue[],
  columnId: string,
  columnName: string
): EncodedCategoricalVariable {
  logger.assert(columnData.length > 0, 'Column data must not be empty');

  // 1. ユニークなカテゴリを抽出（null/undefinedを除外）
  // boolean, number, stringを文字列に変換してカテゴリとして扱う
  const uniqueCategories = new Set<string>();
  for (const value of columnData) {
    if (value !== null && value !== undefined) {
      // boolean, number, stringを文字列に変換
      const stringValue = String(value);
      uniqueCategories.add(stringValue);
    }
  }

  // 2. カテゴリをアルファベット順にソート
  const categories = Array.from(uniqueCategories).sort((a, b) => a.localeCompare(b));

  logger.assert(categories.length >= 2, `Categorical variable must have at least 2 categories, got ${categories.length}`);

  // 3. 参照カテゴリは最初のカテゴリ
  const referenceCategory = categories[0];

  // 4. ダミー変数名を生成（参照カテゴリを除く）
  const dummyVariableNames = categories.slice(1).map(cat => `${columnName}_${cat}`);

  // 5. ダミー変数の列データを作成
  const dummyColumns: number[][] = [];
  for (let i = 1; i < categories.length; i++) {
    const category = categories[i];
    const dummyColumn: number[] = [];

    for (const value of columnData) {
      if (value === null || value === undefined) {
        // 欠損値はそのまま保持（後続の処理で除外される）
        dummyColumn.push(NaN);
      } else {
        // そのカテゴリなら1、それ以外（参照カテゴリを含む）は0
        // 文字列に変換して比較
        dummyColumn.push(String(value) === category ? 1 : 0);
      }
    }

    dummyColumns.push(dummyColumn);
  }

  const encoding: CategoricalEncoding = {
    originalColumnId: columnId,
    originalColumnName: columnName,
    categories,
    referenceCategory,
    dummyVariableNames
  };

  logger.info('[CategoricalEncoder] Encoded categorical variable', {
    columnName,
    nCategories: categories.length,
    nDummyVariables: dummyVariableNames.length,
    referenceCategory,
    categories
  });

  return { encoding, dummyColumns };
}

/**
 * 複数のカテゴリカル変数を一括エンコード
 *
 * @param categoricalData - カテゴリカル変数のデータマップ（列ID -> データ配列）
 * @param columnNames - 列名マップ（列ID -> 列名）
 * @returns エンコード結果の配列
 */
export function encodeCategoricalVariables(
  categoricalData: Map<string, DataValue[]>,
  columnNames: Map<string, string>
): EncodedCategoricalVariable[] {
  const results: EncodedCategoricalVariable[] = [];

  for (const [columnId, data] of categoricalData.entries()) {
    const columnName = columnNames.get(columnId) || columnId;
    const encoded = encodeCategoricalVariable(data, columnId, columnName);
    results.push(encoded);
  }

  return results;
}

/**
 * エンコーディング情報から変数名リストを生成（進捗表示用）
 *
 * @param numericVariableNames - 数値変数の名前リスト
 * @param encodings - カテゴリカル変数のエンコーディング情報リスト
 * @param includeIntercept - 切片を含むかどうか
 * @returns すべての変数名のリスト
 */
export function generateVariableNames(
  numericVariableNames: string[],
  encodings: CategoricalEncoding[],
  includeIntercept: boolean
): string[] {
  const names: string[] = [];

  if (includeIntercept) {
    names.push('Intercept');
  }

  // 数値変数
  names.push(...numericVariableNames);

  // カテゴリカル変数のダミー変数
  for (const encoding of encodings) {
    names.push(...encoding.dummyVariableNames);
  }

  return names;
}
